import React, { useState } from 'react';
import { AudioPreset, Recording, InteractionMode } from '../types';
import { audioEngine } from '../services/audioEngine';

interface ControlsProps {
  isOpen: boolean;
  onToggle: (open: boolean) => void;
  currentPresetName: string;
  savedPresets: AudioPreset[];
  onSelectPreset: (preset: AudioPreset) => void;
  recordings: Recording[];
  onDeleteRecording: (id: string) => void;
  onOpenTutorial: () => void;
  onOpenLegal: () => void;
  interactionMode: InteractionMode;
  onInteractionModeChange: (mode: InteractionMode) => void;
  isMobile: boolean;
  onOpenQR: () => void;
  onOpenFeedback?: () => void;
  onAnalyzeMindfulness: (recording: Recording) => void;
  analyzingRecordingId?: string | null;
  isZenMode?: boolean;
  isCameraActive?: boolean;
  isHUDMinimized?: boolean;
  onOpenMotionGuide?: () => void;
}

const CATEGORIES = ['All', 'Healing', 'Mindfulness', 'Chakra', 'Ambient', 'Acoustic', 'Pluck', 'Space', 'Electronic', 'Retro'];

export const Controls: React.FC<ControlsProps> = ({ 
  isOpen,
  onToggle,
  currentPresetName,
  savedPresets,
  onSelectPreset,
  recordings,
  onDeleteRecording,
  onOpenTutorial,
  interactionMode,
  onInteractionModeChange,
  isMobile,
  onOpenQR,
  onOpenFeedback,
  onAnalyzeMindfulness,
  analyzingRecordingId,
  isZenMode = false,
  isCameraActive = false,
  isHUDMinimized = false,
  onOpenMotionGuide
}) => {
  const [activeTab, setActiveTab] = useState<'library' | 'recordings'>('library');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [previewingPresetName, setPreviewingPresetName] = useState<string | null>(null);

  const handleAudition = async (preset: AudioPreset, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    setPreviewingPresetName(preset.name);
    audioEngine.setPreset(preset);
    await audioEngine.playPreview();
    setTimeout(() => {
      setPreviewingPresetName((current) => (current === preset.name ? null : current));
    }, 1200);
  };

  const handleSelectAndPerform = (preset: AudioPreset) => {
    onSelectPreset(preset);
    onToggle(false);
  };

  const filteredPresets = savedPresets.filter(p => {
    const matchesCategory = selectedCategory === 'All' || p.category === selectedCategory;
    const matchesSearch = !searchQuery.trim() || 
      p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (p.description && p.description.toLowerCase().includes(searchQuery.toLowerCase()));
    return matchesCategory && matchesSearch;
  });

  if (!isOpen) return null;

  return (
    <div 
      id="sound-lab-modal" 
      role="dialog"
      aria-modal="true"
      aria-labelledby="sound-lab-heading"
      className="fixed bottom-0 left-0 w-full bg-slate-950/95 backdrop-blur-2xl border-t border-white/10 p-3.5 sm:p-6 z-[60] transition-transform duration-300 ease-in-out max-h-[85vh] sm:max-h-[82vh] flex flex-col shadow-2xl shadow-black/80 animate-fade-in pb-[max(env(safe-area-inset-bottom,0px),0.75rem)]"
    >
      {/* Header bar: Responsive wrapping for mobile & desktop */}
      <div className="flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-3 mb-3.5 sm:mb-4 shrink-0">
        <div className="flex items-center justify-between sm:justify-start gap-2.5 sm:gap-3 flex-wrap">
          <div className="flex items-center gap-2 shrink-0">
            <div className="w-2.5 h-2.5 rounded-full bg-indigo-500"></div>
            <h2 id="sound-lab-heading" className="text-xs sm:text-sm font-bold tracking-widest text-indigo-300 uppercase">
              Sound Lab
            </h2>
          </div>

          <div className="flex items-center gap-2 border-l border-white/10 pl-2.5 sm:pl-3 flex-wrap">
            <button 
              id="tutorial-btn"
              onClick={onOpenTutorial}
              title="User Guide & Camera / Motion Gesture Help"
              className="p-1.5 px-2.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white transition text-xs flex items-center gap-1.5 border border-slate-700 cursor-pointer shadow-sm active:scale-95 group min-h-[36px]"
              aria-label="Open User Guide and Camera Gesture Help"
            >
              <div className="w-4 h-4 rounded-full bg-indigo-500/20 text-indigo-300 flex items-center justify-center font-bold text-[10px] border border-indigo-500/30 group-hover:bg-indigo-500/40">
                {interactionMode === InteractionMode.CAMERA ? '🖐️' : '?'}
              </div>
              <span className="text-xs font-semibold whitespace-nowrap">
                {interactionMode === InteractionMode.CAMERA ? 'Camera Guide & Help' : 'Guide & Help'}
              </span>
            </button>

            {/* Quick Camera Gesture Tip when in Camera Mode */}
            {interactionMode === InteractionMode.CAMERA && (
              <div 
                className="hidden lg:flex items-center gap-2 px-2.5 py-1 rounded-lg bg-sky-500/10 border border-sky-500/30 text-[11px] text-sky-300 cursor-pointer hover:bg-sky-500/20 transition min-h-[36px]"
                onClick={onOpenTutorial}
                title="Click to view full Camera Gesture Guide"
              >
                <span>🖐️ Camera: ↑↓ Pitch • ↔ Filter • ⚡ Velocity</span>
              </div>
            )}

            {!isMobile && (
              <button 
                id="phone-sync-btn"
                onClick={onOpenQR} 
                className="p-1.5 rounded-lg hover:bg-indigo-500/20 text-indigo-300 transition flex items-center gap-1.5 px-2.5 bg-indigo-500/10 border border-indigo-500/30 text-xs font-semibold min-h-[36px]"
                aria-label="Open Phone Sync QR Code"
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-3.5 h-3.5 shrink-0">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 1.5H8.25A2.25 2.25 0 0 0 6 3.75v16.5a2.25 2.25 0 0 0 2.25 2.25h7.5A2.25 2.25 0 0 0 18 20.25V3.75a2.25 2.25 0 0 0-2.25-2.25H13.5m-3 0V3h3V1.5m-3 0h3m-3 18.75h3" />
                </svg>
                <span className="text-xs font-bold uppercase tracking-wider whitespace-nowrap">Phone Sync</span>
              </button>
            )}

            {onOpenFeedback && (
              <button
                id="library-feedback-btn"
                onClick={onOpenFeedback}
                title="Rate App and Share Feedback (Google Form)"
                className="p-1.5 rounded-lg hover:bg-amber-500/20 text-amber-300 transition flex items-center gap-1.5 px-2.5 bg-amber-500/10 border border-amber-500/30 text-xs font-semibold min-h-[36px] cursor-pointer active:scale-95"
                aria-label="Give Feedback"
              >
                <span className="text-xs">⭐</span>
                <span className="text-xs font-bold uppercase tracking-wider whitespace-nowrap">Feedback</span>
              </button>
            )}
          </div>
        </div>

        <div className="flex items-center justify-between sm:justify-end gap-2 sm:gap-3">
          <div className="flex bg-slate-900 border border-white/10 p-0.5 rounded-xl overflow-hidden flex-1 sm:flex-initial">
            <button 
              id="mode-motion-btn"
              onClick={() => onInteractionModeChange(InteractionMode.MOTION)} 
              className={`flex-1 sm:flex-initial px-2.5 sm:px-3 py-1.5 rounded-lg text-xs font-bold transition text-center min-h-[34px] flex items-center justify-center ${interactionMode === InteractionMode.MOTION ? 'bg-indigo-600 text-white shadow' : 'text-slate-400 hover:text-slate-200'}`}
              title={isMobile ? "Motion Mode (Phone Tilt)" : "Cursor Motion Mode"}
            >
              MOTION
            </button>
            <button 
              id="mode-camera-btn"
              onClick={() => onInteractionModeChange(InteractionMode.CAMERA)} 
              className={`flex-1 sm:flex-initial px-2.5 sm:px-3 py-1.5 rounded-lg text-xs font-bold transition flex items-center justify-center gap-1.5 min-h-[34px] ${interactionMode === InteractionMode.CAMERA ? 'bg-indigo-600 text-white shadow' : 'text-slate-400 hover:text-slate-200'}`}
              title="Camera Motion Tracking Mode (Webcam Body/Hands)"
            >
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-3.5 h-3.5 shrink-0">
                <path strokeLinecap="round" strokeLinejoin="round" d="m15.75 10.5 4.72-4.72a.75.75 0 0 1 1.28.53v11.38a.75.75 0 0 1-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 0 0 2.25-2.25v-9a2.25 2.25 0 0 0-2.25-2.25h-9A2.25 2.25 0 0 0 2.25 7.5v9a2.25 2.25 0 0 0 2.25 2.25Z" />
              </svg>
              <span>CAMERA</span>
            </button>
            {isMobile && (
              <button 
                id="mode-touch-btn"
                onClick={() => onInteractionModeChange(InteractionMode.TOUCH)} 
                className={`flex-1 sm:flex-initial px-2.5 sm:px-3 py-1.5 rounded-lg text-xs font-bold transition text-center min-h-[34px] flex items-center justify-center ${interactionMode === InteractionMode.TOUCH ? 'bg-indigo-600 text-white shadow' : 'text-slate-400 hover:text-slate-200'}`}
                title="Touch & Drag Mode"
              >
                TOUCH
              </button>
            )}
          </div>

          <button 
            id="close-sound-lab-btn"
            onClick={() => onToggle(false)} 
            className="text-slate-400 hover:text-white p-2 hover:bg-white/10 rounded-xl transition cursor-pointer shrink-0 min-h-[38px] min-w-[38px] flex items-center justify-center"
            aria-label="Close Sound Lab"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      {/* Tabs & Search */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-white/10 pb-3 mb-3 shrink-0">
        <div className="flex gap-4">
          <button 
            id="tab-library-btn"
            onClick={() => setActiveTab('library')} 
            className={`pb-1 text-sm font-semibold border-b-2 transition ${activeTab === 'library' ? 'border-indigo-400 text-white' : 'border-transparent text-slate-400 hover:text-slate-200'}`}
          >
            Instruments & Synths ({savedPresets.length})
          </button>
          <button 
            id="tab-recordings-btn"
            onClick={() => setActiveTab('recordings')} 
            className={`pb-1 text-sm font-semibold border-b-2 transition ${activeTab === 'recordings' ? 'border-indigo-400 text-white' : 'border-transparent text-slate-400 hover:text-slate-200'}`}
          >
            Recordings ({recordings.length})
          </button>
        </div>

        {activeTab === 'library' && (
          <div className="relative">
            <input 
              id="search-presets-input"
              type="text" 
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search sounds..." 
              className="bg-slate-900 border border-slate-800 focus:border-indigo-500 rounded-lg pl-8 pr-3 py-1.5 text-xs text-white placeholder-slate-500 focus:outline-none w-full sm:w-48 transition"
            />
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 text-slate-500 absolute left-2.5 top-2">
              <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" />
            </svg>
          </div>
        )}
      </div>

      {/* Category Pills for Library */}
      {activeTab === 'library' && (
        <div className="flex gap-1.5 overflow-x-auto no-scrollbar pb-2.5 mb-2 shrink-0">
          {CATEGORIES.map(cat => (
            <button
              key={cat}
              onClick={() => setSelectedCategory(cat)}
              className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap transition ${
                selectedCategory === cat 
                  ? 'bg-indigo-600 text-white shadow-sm' 
                  : 'bg-slate-900 border border-slate-800 text-slate-400 hover:text-slate-200 hover:border-slate-700'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>
      )}

      {/* Content Area */}
      <div className="overflow-y-auto pr-1 custom-scrollbar flex-1 min-h-0">
        {activeTab === 'library' && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 pb-4">
            {filteredPresets.map((presetItem) => {
              const isActive = currentPresetName === presetItem.name;
              const isPreviewingThis = previewingPresetName === presetItem.name;

              return (
                <div
                  key={presetItem.name}
                  id={`preset-card-${presetItem.name.replace(/\s+/g, '-').toLowerCase()}`}
                  onClick={() => onSelectPreset(presetItem)}
                  className={`p-3.5 rounded-xl border transition cursor-pointer flex flex-col justify-between relative group ${
                    isActive 
                      ? 'bg-indigo-950/40 border-indigo-500/80 shadow-lg shadow-indigo-950/50' 
                      : 'bg-slate-900/80 border-slate-800 hover:border-slate-700 hover:bg-slate-900'
                  }`}
                >
                  {/* Top card header */}
                  <div>
                    <div className="flex items-center justify-between mb-1.5">
                      <div className="flex items-center gap-2">
                        <span 
                          className="w-2.5 h-2.5 rounded-full shrink-0 shadow-sm" 
                          style={{ backgroundColor: presetItem.colorHex || '#6366f1' }}
                        />
                        <h3 className="font-bold text-sm text-white truncate">{presetItem.name}</h3>
                      </div>
                      {isActive && (
                        <span className="text-[10px] font-bold uppercase tracking-wider bg-emerald-500/20 border border-emerald-500/40 text-emerald-300 px-2 py-0.5 rounded-full">
                          Active
                        </span>
                      )}
                    </div>

                    <p className="text-xs text-slate-400 line-clamp-2 mb-2 leading-relaxed">
                      {presetItem.description || 'Rich responsive sound synthesizer preset.'}
                    </p>

                    {/* Metadata tags */}
                    <div className="flex items-center gap-1.5 flex-wrap mb-3">
                      {presetItem.tuningSystem && (
                        <span className="text-[10px] font-bold bg-sky-500/20 text-sky-300 px-2 py-0.5 rounded border border-sky-500/30">
                          {presetItem.tuningSystem}
                        </span>
                      )}
                      {presetItem.ambientBed && presetItem.ambientBed !== 'none' && (
                        <span className="text-[10px] font-medium bg-emerald-500/15 text-emerald-300 px-2 py-0.5 rounded border border-emerald-500/25 flex items-center gap-1">
                          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                          <span>{presetItem.ambientBed}</span>
                        </span>
                      )}
                      <span className="text-[10px] font-semibold bg-slate-800/80 text-slate-300 px-2 py-0.5 rounded border border-white/5 capitalize">
                        {presetItem.oscillatorType}
                      </span>
                      {presetItem.category && (
                        <span className="text-[10px] font-semibold bg-indigo-500/10 text-indigo-300 px-2 py-0.5 rounded border border-indigo-500/20">
                          {presetItem.category}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2 pt-2 border-t border-white/5">
                    <button
                      type="button"
                      onClick={(e) => handleAudition(presetItem, e)}
                      disabled={isPreviewingThis}
                      className={`flex-1 text-xs py-1.5 px-2.5 rounded-lg font-medium transition flex items-center justify-center gap-1.5 border ${
                        isPreviewingThis 
                          ? 'bg-indigo-600 text-white border-indigo-500' 
                          : 'bg-slate-800 hover:bg-slate-700 text-slate-200 border-slate-700'
                      }`}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className={`w-3.5 h-3.5 ${isPreviewingThis ? 'animate-spin' : ''}`}>
                        <path d="M6.3 2.84A1.5 1.5 0 004 4.11v11.78a1.5 1.5 0 002.3 1.27l9.344-5.891a1.5 1.5 0 000-2.538L6.3 2.841z" />
                      </svg>
                      {isPreviewingThis ? 'Auditioning...' : 'Audition'}
                    </button>

                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSelectAndPerform(presetItem);
                      }}
                      className={`text-xs py-1.5 px-3 rounded-lg font-semibold transition ${
                        isActive 
                          ? 'bg-emerald-600 hover:bg-emerald-500 text-white shadow-md shadow-emerald-600/30' 
                          : 'bg-indigo-600 hover:bg-indigo-500 text-white shadow-md shadow-indigo-600/20'
                      }`}
                    >
                      {isActive ? 'Perform' : 'Select'}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {activeTab === 'recordings' && (
          <div className="space-y-4 pb-4">
            {recordings.length === 0 ? (
              <div className="text-center py-8 bg-slate-900/50 rounded-xl border border-slate-800">
                <div className="w-10 h-10 rounded-full bg-red-500/10 text-red-400 flex items-center justify-center mx-auto mb-2 border border-red-500/20">
                  <div className="w-3.5 h-3.5 bg-red-500 rounded-full"></div>
                </div>
                <p className="text-sm font-semibold text-slate-300">No recorded sessions yet</p>
                <p className="text-xs text-slate-500 mt-1 max-w-xs mx-auto">
                  Click the red Record button while performing to capture your live motion music.
                </p>
              </div>
            ) : (
              recordings.map((rec) => (
                <div key={rec.id} className="bg-slate-900 border border-slate-800 rounded-xl p-3.5 flex flex-col gap-2.5 shadow-sm">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2.5">
                    <div className="min-w-0">
                      <span className="text-sm font-bold text-slate-200 block truncate">{rec.name}</span>
                      <span className="text-[10px] text-slate-500 block">
                        {new Date(rec.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap shrink-0">
                      {rec.url && (
                        <a 
                          id={`download-session-${rec.id}`}
                          href={rec.url} 
                          download={`${rec.name || 'motion-music-session'}.webm`}
                          className="text-xs bg-slate-800 hover:bg-slate-700 text-indigo-300 border border-slate-700 px-3 py-1.5 rounded-lg transition shrink-0 font-medium min-h-[32px] flex items-center"
                        >
                          Download
                        </a>
                      )}
                      
                      <button
                        id={`analyze-mindfulness-btn-${rec.id}`}
                        type="button"
                        onClick={() => onAnalyzeMindfulness(rec)}
                        disabled={analyzingRecordingId === rec.id}
                        className="text-xs bg-gradient-to-r from-teal-500/20 to-indigo-500/20 hover:from-teal-500/30 hover:to-indigo-500/30 text-teal-300 hover:text-teal-200 border border-teal-500/40 hover:border-teal-400 px-3 py-1.5 rounded-lg transition flex items-center gap-1.5 font-medium shrink-0 shadow-sm min-h-[32px]"
                        title="Analyze flow state and relaxation levels with Gemini"
                      >
                        {analyzingRecordingId === rec.id ? (
                          <>
                            <span className="w-3 h-3 border-2 border-teal-300 border-t-transparent rounded-full animate-spin"></span>
                            <span>Analyzing Report...</span>
                          </>
                        ) : rec.mindfulnessReport ? (
                          <>
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-3.5 h-3.5 text-teal-400">
                              <path fillRule="evenodd" d="M9 3.5a5.5 5.5 0 100 11 5.5 5.5 0 000-11zM2 9a7 7 0 1112.452 4.391l3.328 3.329a.75.75 0 11-1.06 1.06l-3.329-3.328A7 7 0 012 9z" clipRule="evenodd" />
                            </svg>
                            <span>Mindfulness Report ({rec.mindfulnessReport.flowStateScore}%)</span>
                          </>
                        ) : (
                          <>
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-3.5 h-3.5 text-teal-400">
                              <path fillRule="evenodd" d="M9 4.5a.75.75 0 01.721.544l.813 2.846a3.75 3.75 0 002.576 2.576l2.846.813a.75.75 0 010 1.442l-2.846.813a3.75 3.75 0 00-2.576 2.576l-.813 2.846a.75.75 0 01-1.442 0l-.813-2.846a3.75 3.75 0 00-2.576-2.576l-2.846-.813a.75.75 0 010-1.442l2.846-.813A3.75 3.75 0 007.466 7.89l.813-2.846A.75.75 0 019 4.5z" clipRule="evenodd" />
                            </svg>
                            <span>Analyze Mindfulness Report</span>
                          </>
                        )}
                      </button>

                      <button 
                        id={`delete-session-${rec.id}`}
                        onClick={() => onDeleteRecording(rec.id)} 
                        className="text-xs text-red-400 hover:text-red-300 px-2.5 py-1.5 hover:bg-red-500/10 rounded-lg transition shrink-0 font-medium min-h-[32px] flex items-center"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                  {rec.url && <audio src={rec.url} controls className="w-full h-8" />}
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
};
